import React, { Fragment, useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { useTable } from 'chameleon/ui-stack/ui/base/table';
import { startCase, camelCase } from 'lodash';

import './index.scss';
import ResponsiveView, {
  MobileMedia,
  TabletMedia,
} from 'legacy-src/utility/ResponsiveView';
import { makeTableAriaLabel } from 'legacy-src/utility/accessibility';
import cn from 'classnames';
import {
  renderRow,
  getHeaders,
  mobileColumnTitles,
  getHiddenColumns,
} from './utils';
import VideoResourcePreviewWrapper from 'chameleon-src/components/VideoResourcePreviewWrapper';
import PageTitle from 'legacy/src/components/PageTitle';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import { noDataPlaceholder } from 'chameleon/ui-stack/ui/assessments/util';

const StudentAssessmentsTable = (props) => {
  const {
    data,
    noDataText = 'No items found',
    onAssignmentClick,
    onReviewClick,
    featureFlag,
    studentId,
    router,
    initialSortBy,
    onSortChange,
  } = props;

  const { queryParams } = useNavigation({ router });

  const {
    yoy_results: hideOPSTabFlag,
    show_assessment_practice_icon: showAssessmentPracticeIcon,
    show_attestation_confirmation_dialog: showAttestationFlag,
    remove_action_column: removeReviewButton,
    secure_browser: isLdbEnabled,
  } = featureFlag || {};

  const [pageTitle, setPageTitle] = useState(null);

  const { tableSort, status } = queryParams || {};
  const isResults = status === 'completed';

  useEffect(() => {
    setPageTitle(`My ${startCase(camelCase(status))} Assignments`);
  }, [status]);

  const isMobile = MobileMedia.matches;
  const isTablet = TabletMedia.matches;

  const isMobileStudentScoring = isMobile;

  const headers = getHeaders({ isMobileStudentScoring, isResults });

  const cols = [];
  const hiddenColumns = getHiddenColumns({ isTablet, isMobile });

  const { Table, TableBody, TableColumns, TableHeader, sortBy, tableItems } =
    useTable({
      data: data || [],
      headers,
      initialState: {
        hiddenColumns,
        sortBy: Array.isArray(initialSortBy) ? initialSortBy : [],
      },
    });

  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortBy);
    }
  }, [sortBy]);

  /**
   * do column groupings with tailwind
   */
  const mediumColGroup = (
    <colgroup>
      <col style={{ width: '15%' }} />
      <col style={{ width: '8%' }} />
      <col style={{ width: '8%' }} />
      <col />
      <col style={{ minWidth: '15%' }} />
      <col style={{ minWidth: '16%' }} />
    </colgroup>
  );

  const smallColGroup = (
    <colgroup>
      <col style={{ width: '40%' }} />
      <col />
      <col style={{ width: '22%' }} />
      <col style={{ width: '22%' }} />
    </colgroup>
  );

  const mobileColumns = (columns) => {
    const colsCopy = [...columns];
    let cutOut = colsCopy.splice(3, 1)[0];
    colsCopy.splice(1, 0, cutOut);
    return colsCopy;
  };

  const getMobileColumns = () =>
    mobileColumns(cols).filter((col) =>
      ['assignment', 'time_limit', 'start_date', 'due_date'].includes(
        col && col.id,
      ),
    );

  const tableAriaLabel = makeTableAriaLabel({
    tableName: 'Assignments',
    sortQuery: tableSort,
  });

  const renderRowProps = {
    onAssignmentClick,
    onReviewClick,
    removeReviewButton,
    isLdbEnabled,
    TableColumns,
    props,
    studentId,
    noDataPlaceholder,
    isMobileStudentScoring,
    showAssessmentPracticeIcon,
    hideOPSTabFlag,
    showAttestationFlag,
    isMobile,
    isTablet,
  };

  const renderTableRow = React.useCallback(
    (row) => renderRow({ row, ...renderRowProps }),
    [],
  );

  return (
    <Fragment>
      {pageTitle && <PageTitle titleString={pageTitle} />}
      <div className="StudentAssignments mobile:px-6">
        <ResponsiveView screens={['mobile']}>
          <Table
            className={cn('mt-5 student_assignments_table', {
              'sortable-mobile-table': isMobileStudentScoring,
            })}
            type="mobile_table"
            ariaLabel={tableAriaLabel}
            colGroup={smallColGroup}
            cols={
              isMobileStudentScoring
                ? getMobileColumns()
                : getMobileColumns().map((column) => ({
                    ...column,
                    label: mobileColumnTitles(column.id),
                  }))
            }
          >
            <TableHeader />
            <TableBody>
              {Boolean((tableItems || []).length) &&
                tableItems.map((item) => renderTableRow(item))}
            </TableBody>
          </Table>
        </ResponsiveView>
        <ResponsiveView screens={['medium']}>
          <Table
            colGroup={mediumColGroup}
            className="mt-5 student_assignments_table"
            ariaLabel={tableAriaLabel}
            cols={cols.map((column) => ({
              ...column,
              label: mobileColumnTitles(column.id),
            }))}
          >
            <TableHeader />
            <TableBody>
              {Boolean((tableItems || []).length) &&
                tableItems.map((item) => renderTableRow(item))}
            </TableBody>
          </Table>
        </ResponsiveView>
        <ResponsiveView screens={['large']}>
          <Table
            className="mt-5 student_assignments_table"
            ariaLabel={tableAriaLabel}
            cols={cols}
          >
            <TableHeader />
            <TableBody>
              {Boolean((tableItems || []).length) &&
                tableItems.map((item) => renderTableRow(item))}
            </TableBody>
          </Table>
        </ResponsiveView>
        {!Boolean((tableItems || []).length) && (
          <div
            data-test-id="no-data-text"
            className="w-100 bg-gray-100 rounded-xl border-dashed border-gray-500 border leading-8 text-2xl px-5 py-4 mt-6"
          >
            {noDataText}
          </div>
        )}
      </div>
      <VideoResourcePreviewWrapper />
    </Fragment>
  );
};

export default withRouter(StudentAssessmentsTable);
